import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api';

// List page — the canvas editor lives at /automations/:id
export default function Automations() {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/automations');
      setAutomations(r.data);
    } catch { toast.error('Failed to load automations'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  async function createAutomation() {
    setCreating(true);
    try {
      const r = await api.post('/automations', {
        name: 'Untitled automation',
        nodes: [{
          id: 'trigger', type: 'trigger', position: { x: 80, y: 200 },
          data: { keywords: 'JOIN', match: 'exact' },
        }],
        edges: [],
      });
      navigate(`/automations/${r.data._id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create');
    }
    setCreating(false);
  }

  async function toggleActive(a) {
    try {
      const r = await api.put(`/automations/${a._id}`, { active: !a.active });
      setAutomations(list => list.map(x => x._id === a._id ? { ...x, active: r.data.active } : x));
      toast.success(r.data.active ? 'Automation activated' : 'Automation paused');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    }
  }

  async function remove(a) {
    if (!window.confirm(`Delete "${a.name}"? This cannot be undone.`)) return;
    await api.delete(`/automations/${a._id}`);
    setAutomations(list => list.filter(x => x._id !== a._id));
    toast.success('Deleted');
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">🤖 Automations</h1>
        <button className="btn btn-primary" onClick={createAutomation} disabled={creating}>
          {creating ? 'Creating…' : '+ New Automation'}
        </button>
      </div>

      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20, maxWidth: 640 }}>
        Build visual conversations from a keyword or a Meta template quick reply. The automation walks customers through your canvas — messages,
        questions, buttons, tags and more.
      </p>

      {loading ? (
        <div style={{ color: 'var(--text-muted)' }}>Loading…</div>
      ) : automations.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎨</div>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>No automations yet</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
            Create your first automation and design it on the visual canvas.
          </div>
          <button className="btn btn-primary" onClick={createAutomation} disabled={creating}>
            {creating ? 'Creating…' : '+ New Automation'}
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {automations.map(a => (
            <div key={a._id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{a.name}</div>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                  background: a.active ? 'rgba(34,197,94,0.12)' : 'rgba(100,116,139,0.12)',
                  color: a.active ? 'var(--success)' : 'var(--text-muted)',
                }}>
                  {a.active ? '● Live' : '○ Paused'}
                </span>
              </div>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {a.trigger_type === 'template_button'
                  ? <code style={{ background: 'var(--surface2)', padding: '2px 8px', borderRadius: 6, fontSize: 12 }}>
                      {a.trigger_template_name || 'Select a template'} · button reply
                    </code>
                  : (a.trigger_keywords || []).length === 0
                  ? <span style={{ color: 'var(--warning)', fontSize: 12 }}>⚠️ No trigger keywords set</span>
                  : a.trigger_keywords.map(k => (
                    <code key={k} style={{ background: 'var(--surface2)', padding: '2px 8px', borderRadius: 6, fontSize: 12 }}>{k}</code>
                  ))}
              </div>

              <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                ▶ {a.stats?.started || 0} started &nbsp;·&nbsp; ✔ {a.stats?.completed || 0} completed
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
                <button className="btn btn-primary btn-sm" onClick={() => navigate(`/automations/${a._id}`)}>
                  🎨 Open Canvas
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => toggleActive(a)}>
                  {a.active ? 'Pause' : 'Activate'}
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => remove(a)}>Del</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
